import { getGoogleClientId } from './googleConfig';

const SCOPES = [
  'https://www.googleapis.com/auth/calendar.readonly',
  'https://www.googleapis.com/auth/calendar.events',
].join(' ');

const TOKEN_KEY = 'mytasklog_gcal_token';
const CONSENT_KEY = 'mytasklog_gcal_consent';
const API_BASE = 'https://www.googleapis.com/calendar/v3';

// 期限の何秒前から取り直しにいくか
const REFRESH_MARGIN_SEC = 600; // トークン有効期限の10分前にリフレッシュ

// ==========================================================
// 書き込みの間隔と再試行
//
// 予定や実績をまとめて送るとき、一度に投げるとGoogleに弾かれる。
// 投げ始める時刻をずらして、1秒あたり4〜5件に収める。
// 読み込みは1回で250件返るので、こちらは通さない。
// ==========================================================
const MIN_WRITE_INTERVAL_MS = 220;
const MAX_RETRY = 5;
const FIRST_WAIT_MS = 500;

let nextSlotAt = 0;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const pace = async () => {
  const now = Date.now();
  const slot = Math.max(now, nextSlotAt);
  nextSlotAt = slot + MIN_WRITE_INTERVAL_MS;
  if (slot > now) await sleep(slot - now);
};

/** pace() のグローバル状態をリセット（新しい読み込みセッション開始時） */
export const resetPace = () => {
  nextSlotAt = 0;
};

/** 混雑によるもの。待って投げ直せば通る */
const RATE_REASONS = new Set([
  'rateLimitExceeded',
  'userRateLimitExceeded',
  'quotaExceeded',
  'backendError',
]);

const reasonOf = (body) => {
  try {
    const d = JSON.parse(body);
    return d?.error?.errors?.[0]?.reason || '';
  } catch {
    return '';
  }
};

let tokenClient = null;
let tokenClientId = null;
let refreshPromise = null;

// ==========================================================
// 許可の記録（トークンとは別に保持する）
// ==========================================================
export const isConsented = () => localStorage.getItem(CONSENT_KEY) === '1';
const setConsented = (v) => {
  if (v) localStorage.setItem(CONSENT_KEY, '1');
  else localStorage.removeItem(CONSENT_KEY);
};

// ==========================================================
// トークン
// ==========================================================
const saveToken = (token, expiresInSec) => {
  localStorage.setItem(
    TOKEN_KEY,
    JSON.stringify({
      token,
      expiresAt: Date.now() + (expiresInSec - REFRESH_MARGIN_SEC) * 1000,
    })
  );
};

const readToken = () => {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data.token || Date.now() >= data.expiresAt) return null;
    return data.token;
  } catch {
    return null;
  }
};

const clearTokenOnly = () => localStorage.removeItem(TOKEN_KEY);

export const hasLiveToken = () => readToken() !== null;
export const hasValidToken = () => isConsented();

// ==========================================================
// GIS の読み込み
// ==========================================================
const loadGis = () =>
  new Promise((resolve, reject) => {
    if (window.google?.accounts?.oauth2) return resolve();

    const existing = document.getElementById('gis-script');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('Googleのスクリプトを読み込めませんでした'))
      );
      return;
    }

    const s = document.createElement('script');
    s.id = 'gis-script';
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Googleのスクリプトを読み込めませんでした'));
    document.head.appendChild(s);
  });

const createClient = () => {
  const clientId = getGoogleClientId();
  if (!clientId) return null;
  if (tokenClient && tokenClientId === clientId) return tokenClient;
  if (!window.google?.accounts?.oauth2) return null;

  tokenClient = window.google.accounts.oauth2.initTokenClient({
    client_id: clientId,
    scope: SCOPES,
    callback: () => {},
  });
  tokenClientId = clientId;
  return tokenClient;
};

/** 起動時に呼んでおく。ボタンを押した瞬間に待たずに要求を出せるようにする */
export const preloadGoogle = async () => {
  try {
    await loadGis();
    createClient();
  } catch {
    /* 失敗しても無視。使うときに再試行される */
  }
};

const ensureClient = async () => {
  const c = createClient();
  if (c) return c;
  await loadGis();
  const c2 = createClient();
  if (!c2) throw new Error('Googleの準備ができていません（接続設定を確認してください）');
  return c2;
};

// ==========================================================
// どのアカウントで接続しているか
//
// ブラウザに複数のGoogleアカウントがあると、どれを使うか指定しない限り
// トークンを取り直すたびにアカウント選択の画面が出る。
// そこで、カレンダーを許可したアカウントを覚えておき、login_hint で渡す。
//
// 覚えるのは「カレンダーを許可したアカウント」。
// ログイン（Firebase）とカレンダーで別のアカウントを使っていても取り違えない。
// まだ覚えていないときだけ、ログイン中のメールアドレスで代用する。
// ==========================================================
const HINT_KEY = 'mytasklog_gcal_hint';

const readHint = () => localStorage.getItem(HINT_KEY) || '';
const saveHint = (v) => {
  if (v) localStorage.setItem(HINT_KEY, v);
  else localStorage.removeItem(HINT_KEY);
};

let fallbackHint = '';

/** ログイン中のメールアドレスを渡しておく（覚えたアカウントが無いときの代用） */
export const setFallbackLoginHint = (email) => {
  fallbackHint = email || '';
};

const currentHint = () => readHint() || fallbackHint;

/**
 * 許可したアカウントのメールアドレスを覚える。
 * 主カレンダーのIDはそのアカウントのメールアドレスになっている。
 * 失敗しても接続そのものには影響しないので、待たずに投げておく。
 */
const rememberAccount = (token) => {
  fetch(`${API_BASE}/calendars/primary`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((d) => {
      if (d && typeof d.id === 'string' && d.id.includes('@')) saveHint(d.id);
    })
    .catch(() => {
      /* 覚えられなくても次回また試す */
    });
};

/**
 * トークンの取り方。
 *   silent    … 期限切れの取り直し。画面は出さない（出ても一瞬）
 *   reconnect … 接続の更新。アカウントは覚えているものを使う
 *   first     … 初めての接続。アカウントを選んでもらい、同意も取る
 *
 * select_account を付けるのは first だけ。
 * それ以外は login_hint でアカウントを指定し、選択画面を飛ばす。
 *
 * アカウントを変えたいときは、いったん「解除」してから接続し直す。
 * 解除で覚えたアカウントも消えるので、次は選び直しになる。
 */
const PROMPT_OF = {
  silent: '',
  reconnect: '',
  first: 'consent select_account',
};

/** 実際の要求。呼ぶ前に client が用意されている必要がある */
const runTokenRequest = (mode) =>
  new Promise((resolve, reject) => {
    const client = tokenClient;
    const choosing = mode === 'first';

    client.callback = (res) => {
      if (res.error) {
        reject(new Error(res.error_description || res.error));
        return;
      }
      saveToken(res.access_token, res.expires_in);
      setConsented(true);
      // 選び直したとき、またはまだ覚えていないときは、アカウントを覚える
      if (choosing || !readHint()) rememberAccount(res.access_token);
      resolve(res.access_token);
    };
    client.error_callback = (err) =>
      reject(new Error(err?.message || err?.type || '認証に失敗しました'));

    const options = { prompt: PROMPT_OF[mode] };
    const hint = choosing ? '' : currentHint();
    if (hint) options.login_hint = hint;

    // ここは同期的に呼ぶ（間に待ち時間を挟むと操作扱いされなくなる）
    client.requestAccessToken(options);
  });

/**
 * ユーザー操作から呼ぶ：Googleと接続。
 *   初めてなら、アカウントを選んで同意してもらう。
 *   すでに許可済みなら、覚えているアカウントでそのまま取り直す。
 */
export const connectGoogle = async () => {
  await ensureClient();
  return runTokenRequest(isConsented() ? 'reconnect' : 'first');
};

/** 接続を解除 */
export const disconnectGoogle = () => {
  const raw = localStorage.getItem(TOKEN_KEY);
  try {
    const token = raw ? JSON.parse(raw).token : null;
    if (token && window.google?.accounts?.oauth2) {
      window.google.accounts.oauth2.revoke(token);
    }
  } catch {
    /* 失敗しても無視 */
  }
  clearTokenOnly();
  setConsented(false);
  // 次に接続するときは、アカウントを選び直してもらう
  saveHint('');
};

/**
 * ボタンを押した瞬間に、待たずに呼ぶための入り口。
 * 途中で待ち時間を挟むと操作扱いされなくなるため、
 * 準備済みのクライアントを使って同期的に要求を出す。
 */
export const refreshOnGesture = () => {
  if (!isConsented()) return null;
  if (readToken()) return null; // まだ有効
  if (refreshPromise) return refreshPromise;
  if (!tokenClient) return null; // 準備前

  refreshPromise = runTokenRequest('silent')
    .then(() => true)
    .catch((e) => {
      console.warn('接続の更新に失敗:', e.message);
      return false;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

/**
 * 【事前更新】トークンが必要ならリフレッシュを開始（待たずに返す）
 * 手動更新時に呼び出し、pace() の 220ms でリフレッシュが完了するタイミングを狙う
 */
export const refreshTokenIfNeeded = () => {
  if (!isConsented()) return null;
  if (readToken()) return null; // まだ有効
  // トークン期限切れ → refreshOnGesture() で非同期リフレッシュ開始
  return refreshOnGesture();
};

// ==========================================================
// API
// ==========================================================
const call = async (path, options = {}) => {
  const token = readToken();
  if (!token) throw new Error('カレンダーの接続が切れています。更新ボタンを押してください。');

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });

  if (res.status === 401) {
    clearTokenOnly();
    throw new Error('カレンダーの接続が切れています。更新ボタンを押してください。');
  }

  if (res.status === 204) return null;
  if (!res.ok) {
    const body = await res.text();
    const reason = reasonOf(body);
    const err = new Error(`Google API エラー (${res.status}): ${body.slice(0, 160)}`);
    err.status = res.status;
    err.reason = reason;

    if (res.status === 403 && !RATE_REASONS.has(reason)) {
      // 混雑ではなく、権限そのものが無い。つなぎ直してもらう。
      // ここを混雑と一緒にすると、直らない待機を延々と繰り返すことになる。
      clearTokenOnly();
      err.code = 'auth';
      err.message = 'カレンダーへの権限がありません。再接続してください。';
    } else if (
      res.status === 429 ||
      res.status >= 500 ||
      (res.status === 403 && RATE_REASONS.has(reason))
    ) {
      err.code = 'rate-limit';
    }

    throw err;
  }
  return res.json();
};

/**
 * 読み込み用。間隔は空けないが、混雑で弾かれたら待って投げ直す。
 * 読み込みは1回で最大2500件返るので、回数自体は多くならない。
 */
const readCall = async (path, options) => {
  let wait = FIRST_WAIT_MS;

  for (let attempt = 0; ; attempt += 1) {
    try {
      return await call(path, options);
    } catch (e) {
      if (e.code !== 'rate-limit' || attempt >= MAX_RETRY) throw e;
      await sleep(wait);
      wait *= 2;
    }
  }
};

/**
 * 書き込み用。間隔を空けて投げ、混雑で弾かれたら待って投げ直す。
 * 0.5秒 → 1秒 → 2秒 → 4秒 → 8秒 と伸ばして最大5回。
 */
const writeCall = async (path, options) => {
  let wait = FIRST_WAIT_MS;

  for (let attempt = 0; ; attempt += 1) {
    await pace();
    try {
      return await call(path, options);
    } catch (e) {
      if (e.code !== 'rate-limit' || attempt >= MAX_RETRY) throw e;
      await sleep(wait);
      wait *= 2;
    }
  }
};

export const listCalendars = async () => {
  const data = await readCall('/users/me/calendarList?minAccessRole=reader');
  return (data.items || []).map((c) => ({
    id: c.id,
    name: c.summaryOverride || c.summary,
    primary: !!c.primary,
    canWrite: c.accessRole === 'owner' || c.accessRole === 'writer',
  }));
};

/**
 * 期間内の予定をすべて取る。
 *
 * 1回で最大2500件まで返る。取り込みを100日先まで広げると
 * 250件では足りなくなるので、続きがあれば続けて取る。
 * 2ページ目以降は間隔を空けて投げる。
 */
const MAX_EVENT_PAGES = 20;

export const listEvents = async (calendarId, timeMin, timeMax) => {
  const items = [];
  let pageToken = null;

  for (let page = 0; page < MAX_EVENT_PAGES; page += 1) {
    const params = new URLSearchParams({
      timeMin: timeMin.toISOString(),
      timeMax: timeMax.toISOString(),
      singleEvents: 'true',
      orderBy: 'startTime',
      maxResults: '2500',
    });
    if (pageToken) {
      params.set('pageToken', pageToken);
      await pace();
    }

    const data = await readCall(
      `/calendars/${encodeURIComponent(calendarId)}/events?${params}`
    );
    items.push(...(data.items || []));

    pageToken = data.nextPageToken;
    if (!pageToken) break;
  }

  return items;
};

export const listAllEventsInRange = async (calendarId, timeMin, timeMax) => {
  // 新しい読み込みセッションを開始するため、pace() の状態をリセット
  resetPace();
  // 指定された時間範囲内のすべてのイベントを取得（ページング対応）
  return listEvents(calendarId, timeMin, timeMax);
};

export const insertEvent = (calendarId, event) =>
  writeCall(`/calendars/${encodeURIComponent(calendarId)}/events`, {
    method: 'POST',
    body: JSON.stringify(event),
  });

export const patchEvent = (calendarId, eventId, event) =>
  writeCall(`/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`, {
    method: 'PATCH',
    body: JSON.stringify(event),
  });

export const deleteEvent = (calendarId, eventId) =>
  writeCall(`/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`, {
    method: 'DELETE',
  });
